import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import SeverityBadge from "@/components/SeverityBadge";
import { toast } from "sonner";
import { Terminal, Upload, Play, Square, Search } from "lucide-react";

const SEVS = ["all", "critical", "error", "warning", "info"];

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("all");
  const [paste, setPaste] = useState("");
  const [streaming, setStreaming] = useState(false);

  const load = async () => {
    const { data } = await api.get("/logs", {
      params: { limit: 250, severity: severity === "all" ? undefined : severity, search: search || undefined },
    });
    setLogs(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [severity]);

  const submitSearch = (e) => { e.preventDefault(); load(); };

  const submitPaste = async () => {
    if (!paste.trim()) return;
    try {
      const { data } = await api.post("/logs/paste", { content: paste, source: "manual" });
      toast.success(`Ingested ${data.inserted} lines`);
      setPaste("");
      load();
    } catch (e) {
      toast.error("Ingestion failed");
    }
  };

  const startStream = async () => {
    if (streaming) return;
    setStreaming(true);
    const token = localStorage.getItem("forensiq_token");
    try {
      const res = await fetch(`${API_BASE}/logs/stream`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const dataLine = p.split("\n").find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          try {
            const entry = JSON.parse(dataLine.slice(5).trim());
            setLogs((prev) => [entry, ...prev].slice(0, 300));
          } catch (_e) { /* skip malformed frame */ }
        }
      }
    } catch (e) {
      toast.error("Stream ended");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Centralized log ingestion</div>
          <h1 className="font-display font-black text-5xl mt-2">Logs</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={startStream}
            disabled={streaming}
            data-testid="stream-toggle"
            className={`inline-flex items-center gap-2 border px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest transition-colors ${
              streaming ? "border-[#FF9F0A] text-[#FF9F0A]" : "border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black"
            }`}
          >
            {streaming ? <><Square className="w-3.5 h-3.5" /> streaming…</> : <><Play className="w-3.5 h-3.5" /> Simulate live stream</>}
          </button>
        </div>
      </header>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <form onSubmit={submitSearch} className="flex-1 min-w-[280px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E9196]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Plain-language search: e.g. 'failed root login', 'SQL injection', '185.220'"
            data-testid="log-search"
            className="w-full bg-transparent border border-white/15 pl-10 pr-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF]"
          />
        </form>
        <div className="flex items-center border border-white/15 rounded-sm overflow-hidden" data-testid="severity-filter">
          {SEVS.map((s) => (
            <button
              key={s}
              onClick={() => setSeverity(s)}
              className={`px-3 py-2 font-mono uppercase text-[11px] tracking-widest transition-colors ${
                severity === s ? "bg-white/10 text-white" : "text-[#8E9196] hover:text-white"
              }`}
            >{s}</button>
          ))}
        </div>
      </div>

      {/* Paste ingest */}
      <div className="mb-6 border border-white/10 bg-[#121212] rounded-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">
            <Upload className="w-3.5 h-3.5" /> Paste raw logs (syslog / CEF / JSON / freeform)
          </div>
          <button
            onClick={submitPaste}
            disabled={!paste.trim()}
            data-testid="paste-ingest"
            className="border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-1 rounded-sm font-mono uppercase text-[11px] tracking-widest disabled:opacity-50"
          >
            Ingest
          </button>
        </div>
        <textarea
          rows={4}
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          placeholder={"paste one log line per row…\n<134>Jul 13 08:57:22 srv-web-02 kernel: SYN flood on port 443"}
          data-testid="paste-textarea"
          className="w-full bg-transparent p-4 font-mono text-xs outline-none resize-none placeholder:text-[#3f3f46]"
        />
      </div>

      {/* Terminal-style log stream */}
      <div className="relative border border-white/10 bg-[#0a0a0a] rounded-sm scan-lines overflow-hidden" data-testid="logs-terminal">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 bg-[#121212]">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">
            <Terminal className="w-3.5 h-3.5" /> stream · {logs.length} events
          </div>
          <div className="flex items-center gap-1">
            {["#FF3B30","#FF9F0A","#34C759"].map((c) => <span key={c} className="w-2 h-2 rounded-full" style={{ background: c }} />)}
          </div>
        </div>
        <div className="max-h-[calc(100vh-360px)] min-h-[300px] overflow-y-auto p-3 font-mono text-[12px] leading-relaxed">
          {logs.length === 0 && <div className="text-[#52525B] p-4">No logs match.</div>}
          {logs.map((l) => (
            <div key={l.id} className="grid grid-cols-[130px_90px_150px_1fr] gap-3 py-1 border-b border-white/[0.03] hover:bg-white/[0.02]">
              <span className="text-[#52525B]">{new Date(l.timestamp).toISOString().slice(11, 19)}</span>
              <SeverityBadge severity={l.severity} />
              <span className="text-[#00F0FF] truncate">{l.host || "-"}</span>
              <span className="text-white truncate">{l.message}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
